import React, { useMemo, useState } from 'react';
import { opsApi, type LiveProcess, type LiveServer } from '../../../api/dbOps';
import { errorText } from '../../../api/migrationRunner';
import { Alert, Btn, Dialog, ErrorBox, Panel, Pill, Spinner } from '../runner/ui';
import { Empty, ServerPicker, secs, td, th, usePoll } from './shared';

/**
 * Live activity (approved mockup "Database redesign", Live activity): what
 * every database server is running right now, refreshed every 5 s. Stop
 * query (owner) ends the running statement, not the connection.
 */
const LiveTab: React.FC<{ isOwner: boolean }> = ({ isOwner }) => {
  const [server, setServer] = useState('');
  const [showIdle, setShowIdle] = useState(false);
  const [stop, setStop] = useState<{ server: LiveServer; p: LiveProcess } | null>(null);
  const [stopping, setStopping] = useState(false);
  const [stopErr, setStopErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const servers = usePoll(() => opsApi.servers(), null);
  const live = usePoll(() => opsApi.live(server || undefined), 5000, [server]);

  const doStop = async () => {
    if (!stop) return;
    setStopping(true);
    setStopErr(null);
    try {
      await opsApi.kill(stop.server.server.key, stop.p.id);
      setNotice(`Stopped the query on connection ${stop.p.id}.`);
      setStop(null);
      void live.reload();
    } catch (err) {
      setStopErr(errorText(err));
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <ServerPicker servers={servers.data || []} value={server} onChange={setServer} />
        <label className="flex items-center gap-1.5 text-[13px] text-slate-600">
          <input type="checkbox" checked={showIdle} onChange={(e) => setShowIdle(e.target.checked)} /> Show idle connections
        </label>
        <span className="flex-1" />
        <span className="flex items-center gap-1.5 text-[12.5px] text-slate-500">{live.loading && <Spinner className="h-3.5 w-3.5" />}Updates every 5 s</span>
      </div>
      {notice && <Alert tone="ok" title={notice} action={<Btn kind="ghost" small onClick={() => setNotice(null)}>Dismiss</Btn>} />}
      {live.error && !live.data ? <ErrorBox>{errorText(live.error)}</ErrorBox> : null}
      {(live.data || []).map((s) => (
        <ServerPanel key={s.server.key} s={s} showIdle={showIdle} isOwner={isOwner} onStop={(p) => { setStopErr(null); setStop({ server: s, p }); }} />
      ))}
      {live.data && !live.data.length && <Empty>No database servers found.</Empty>}

      <Dialog
        open={!!stop}
        onClose={() => setStop(null)}
        title="Stop this query?"
        footer={(
          <>
            <Btn kind="ghost" onClick={() => setStop(null)}>Keep it running</Btn>
            <Btn kind="danger" disabled={stopping} onClick={doStop}>{stopping ? <Spinner /> : null}Stop query</Btn>
          </>
        )}
      >
        {stop && (
          <>
            <p>
              Connection <b>{stop.p.id}</b> on {stop.server.server.label}
              {stop.p.company ? <> ({stop.p.company.name})</> : null}, running for {secs(stop.p.seconds)}.
            </p>
            <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-100 p-3 font-mono text-[12px]">{stop.p.query}</pre>
            <p className="text-[12.5px] text-slate-500">Only the statement stops; the app's connection stays open and its transaction rolls back. This is recorded in the audit log.</p>
            {stopErr && <ErrorBox>{stopErr}</ErrorBox>}
          </>
        )}
      </Dialog>
    </div>
  );
};

const ServerPanel: React.FC<{ s: LiveServer; showIdle: boolean; isOwner: boolean; onStop: (p: LiveProcess) => void }> = ({ s, showIdle, isOwner, onStop }) => {
  const blockers = useMemo(() => new Set((s.locks?.waits || []).map((w) => w.blocking_id).filter((x): x is number => x !== null)), [s.locks]);
  const rows = (s.processes || []).filter((p) => !p.system && (showIdle || p.command !== 'Sleep'));
  const c = s.connections;
  return (
    <Panel
      title={s.server.label}
      bodyClass="p-0"
      actions={s.ok ? (
        <>
          <Pill tone={c?.percent !== null && c?.percent !== undefined && c.percent >= 80 ? 'warn' : 'ok'}>
            {c?.current ?? '?'} connections of {c?.max ?? '?'}
          </Pill>
          {(s.summary?.lock_waits || 0) > 0 && <Pill tone="warn">{s.summary?.lock_waits} waiting on a lock</Pill>}
        </>
      ) : <Pill tone="bad">unreachable</Pill>}
    >
      {!s.ok ? (
        <div className="p-4"><ErrorBox>{s.error}</ErrorBox></div>
      ) : rows.length === 0 ? (
        <div className="p-4 text-[13px] text-slate-500">Nothing running right now.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr><th className={th}>Database</th><th className={th}>Running</th><th className={th}>For</th><th className={th}>State</th><th className={th} /></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((p) => {
                const holding = blockers.has(p.id);
                return (
                  <tr key={p.id}>
                    <td className={td}>
                      <div className="font-medium text-slate-900">{p.db || '—'}</div>
                      <div className="text-[11.5px] text-slate-500">{p.company?.name || p.user} · #{p.id}</div>
                    </td>
                    <td className={`${td} max-w-[480px]`}>
                      {p.query ? <code className="line-clamp-2 break-all font-mono text-[12px]" title={p.query}>{p.query}</code> : <span className="text-slate-400">{p.command}</span>}
                    </td>
                    <td className={`${td} whitespace-nowrap tabular-nums`}>{secs(p.seconds)}</td>
                    <td className={td}>
                      {holding ? <Pill tone="bad">holding the lock</Pill>
                        : p.waiting_for_lock ? <Pill tone="warn">waiting for a lock</Pill>
                          : p.command === 'Sleep' ? <Pill tone="mut">idle</Pill>
                            : <Pill tone="ok">{p.state || 'running'}</Pill>}
                    </td>
                    <td className={`${td} text-right`}>
                      {isOwner && p.query && p.command !== 'Sleep' && (
                        <Btn kind={holding ? 'danger' : 'ghost'} small onClick={() => onStop(p)}>Stop query</Btn>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {s.ok && (s.locks?.waits.length || 0) > 0 && (
        <div className="border-t border-slate-200 bg-amber-50 px-4 py-2 text-[12.5px] text-amber-900">
          {s.locks?.waits.map((w, i) => (
            <div key={i}>
              Connection {w.waiting_id} waits {w.seconds !== null ? secs(w.seconds) : ''} for {w.lock_type || 'a lock'} on {w.object || 'a table'}
              {w.blocking_id ? <>, held by connection {w.blocking_id}</> : null}.
            </div>
          ))}
          <div className="text-amber-700">Lock data from {s.locks?.source === 'processlist' ? 'the process list (turn on performance_schema to see who holds it)' : s.locks?.source}.</div>
        </div>
      )}
    </Panel>
  );
};

export default LiveTab;
